import React, { useEffect, useState } from "react";
import { trainingApi } from "../../../api/training";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { copyTextToClipboard } from "../../utils/copyTextToClipboard";
import { HseTabBar } from "../hse/HseTabBar";
import {
  buttonClass,
  primaryClass,
  inputClass,
  Field,
} from "./trainingAdminShared";
import {
  parseTrainingGuideJson,
  serializeTrainingGuideJson,
  applyTrainingGuideContent,
  SAMPLE_TRAINING_GUIDE_JSON,
  validateTrainingAudience,
  validateTrainingDocuments,
} from "./trainingGuideJson";
import { buildTrainingGuideAiGuide, loadTrainingDocuments, loadTrainingPositions } from "./trainingGuideAiGuide";
import PMSwitch from "../../utils/PMSwitch";

const errorKeys = {
  invalid: "trainingAdmin.jsonInvalid",
  unsupported: "trainingAdmin.jsonUnsupported",
  size: "trainingAdmin.jsonSize",
  identity: "trainingAdmin.jsonIdentity",
  answers: "trainingAdmin.jsonAnswers",
  dependency: "trainingAdmin.jsonDependency",
  reference: "trainingAdmin.jsonReference",
  audience: "trainingAdmin.jsonAudienceInvalid",
  positionsUnavailable: "trainingAdmin.jsonPositionsFailed",
  language: "trainingAdmin.jsonLanguage",
  languageMismatch: "trainingAdmin.jsonLanguageMismatch",
  translations: "trainingAdmin.jsonTranslations",
  documentUnknown: "trainingAdmin.jsonDocumentUnknown",
  documentsUnavailable: "trainingAdmin.jsonDocumentsFailed",
};

export default function TrainingGuideJsonDialog({
  draft,
  disabled = false,
  onApply,
  onClose,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("json");
  const [text, setText] = useState(() => serializeTrainingGuideJson(draft));
  const [mode, setMode] = useState("replace");
  const [review, setReview] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [positions, setPositions] = useState(null);
  const [positionsFailed, setPositionsFailed] = useState(false);
  const [positionsAttempt, setPositionsAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setPositions(null); setPositionsFailed(false); setReview(null);
    loadTrainingPositions(trainingApi.options, () => active)
      .then((rows) => { if (active) setPositions(rows); })
      .catch(() => { if (active) setPositionsFailed(true); });
    return () => { active = false; };
  }, [positionsAttempt]);
  // Company HMS documents for the AI instructions (step 5): version IDs, numbers, chapters
  // and — when chosen — short excerpts, so AI can build guides on the company's routines.
  const [documents, setDocuments] = useState(null);
  const [documentsFailed, setDocumentsFailed] = useState(false);
  const [documentsAttempt, setDocumentsAttempt] = useState(0);
  const [withExcerpts, setWithExcerpts] = useState(false);
  useEffect(() => {
    let active = true;
    setDocuments(null); setDocumentsFailed(false);
    loadTrainingDocuments(trainingApi.options, () => active, { withExcerpt: withExcerpts })
      .then((rows) => { if (active) setDocuments(rows); })
      .catch(() => { if (active) setDocumentsFailed(true); });
    return () => { active = false; };
  }, [documentsAttempt, withExcerpts]);
  const aiGuide = positions && documents ? buildTrainingGuideAiGuide(positions, documents) : "";
  const changeText = (next) => {
    setText(next);
    setReview(null);
    setError(null);
    setMessage(null);
  };
  const copy = async (value) => {
    setError(null);
    setMessage(null);
    try {
      await copyTextToClipboard(value);
      setMessage(t("trainingAdmin.jsonCopied"));
    } catch {
      setError(t("trainingAdmin.jsonClipboardFailed"));
    }
  };
  const showError = (failure) =>
    setError(t(errorKeys[failure?.code] || "trainingAdmin.jsonInvalid"));
  const inspect = () => {
    setError(null);
    setMessage(null);
    setReview(null);
    try {
      const content = parseTrainingGuideJson(text);
      validateTrainingAudience(content, positions);
      validateTrainingDocuments(content, documents);
      applyTrainingGuideContent(draft, content, mode);
      setReview(content);
    } catch (failure) {
      showError(failure);
    }
  };
  const apply = () => {
    if (!review || disabled) return;
    try {
      validateTrainingAudience(review, positions);
      validateTrainingDocuments(review, documents);
      onApply(review, mode);
    } catch (failure) {
      showError(failure);
    }
  };
  return (
    <PMDialog
      open
      onClose={onClose}
      size="5xl"
      title={t("trainingAdmin.jsonTools")}
      closeOnOutsideClick
    >
      <div className="space-y-4">
        <HseTabBar
          tabs={[
            { id: "json", label: t("trainingAdmin.jsonContent") },
            { id: "ai", label: t("trainingAdmin.jsonAi") },
          ]}
          activeId={tab}
          onSelect={(item) => setTab(item.id)}
          ariaLabel={t("trainingAdmin.jsonTools")}
        />
        <p className="text-sm leading-6 text-slate-600">
          {t("trainingAdmin.jsonScope")}
        </p>
        {error && (
          <p
            role="alert"
            className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950"
          >
            {error}
          </p>
        )}
        {message && (
          <p
            role="status"
            className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-900"
          >
            {message}
          </p>
        )}
        {tab === "json" ? (
          <>
            <Field label={t("trainingAdmin.jsonContent")}>
              <textarea
                className={`${inputClass} min-h-[280px] font-mono text-xs`}
                rows={14}
                value={text}
                onChange={(event) => changeText(event.target.value)}
                spellCheck={false}
              />
            </Field>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={buttonClass}
                onClick={() => copy(text)}
              >
                {t("trainingAdmin.jsonCopy")}
              </button>
              <button
                type="button"
                className={buttonClass}
                onClick={() => changeText(serializeTrainingGuideJson(draft))}
              >
                {t("trainingAdmin.jsonReload")}
              </button>
              <button
                type="button"
                className={buttonClass}
                onClick={() => changeText(SAMPLE_TRAINING_GUIDE_JSON)}
              >
                {t("trainingAdmin.jsonExample")}
              </button>
            </div>
            <fieldset
              disabled={disabled}
              className="space-y-3 rounded-xl border border-slate-200 p-4"
            >
              <legend className="px-2 text-sm font-semibold">
                {t("trainingAdmin.jsonImport")}
              </legend>
              <div className="flex flex-wrap gap-4">
                {[
                  ["replace", "trainingAdmin.jsonReplace"],
                  ["append", "trainingAdmin.jsonAppend"],
                ].map(([value, label]) => (
                  <label
                    className="flex min-h-[44px] items-center gap-2 text-sm"
                    key={value}
                  >
                    <input
                      type="radio"
                      name="training-json-mode"
                      checked={mode === value}
                      onChange={() => {
                        setMode(value);
                        setReview(null);
                        setError(null);
                      }}
                    />
                    {t(label)}
                  </label>
                ))}
              </div>
              <p className="text-sm text-slate-600">
                {t(
                  mode === "replace"
                    ? "trainingAdmin.jsonReplaceHint"
                    : "trainingAdmin.jsonAppendHint",
                )}
              </p>
              <button type="button" className={buttonClass} onClick={inspect}>
                {t("trainingAdmin.jsonReview")}
              </button>
              {review && (
                <section
                  className="space-y-3 rounded-xl bg-slate-50 p-4"
                  aria-label={t("trainingAdmin.jsonReview")}
                >
                  <h3 className="font-semibold">
                    {mode === "replace"
                      ? (review.title ?? draft.guide.title)
                      : draft.guide.title}
                  </h3>
                  <p className="text-sm">
                    {t("trainingAdmin.jsonReviewSummary", {
                      pages: review.pages.length,
                      cards: review.pages.flatMap((page) => page.cards).length,
                    })}
                  </p>
                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm" role="status">
                    <p className="font-semibold">{t(review.audience ? "trainingAdmin.jsonAudienceChanges" : "trainingAdmin.jsonAudienceUnchanged")}</p>
                    {review.audience?.type === "all" && <p>{t("trainingAdmin.allUsers")}</p>}
                    {review.audience?.type === "positions" && <ul className="mt-1 list-disc pl-5">{review.audience.position_ids.map((id) => <li key={id}>{positions.find((row) => row.id === id)?.title} (ID {id})</li>)}</ul>}
                  </div>
                  <ol className="list-decimal space-y-1 pl-5 text-sm">
                    {review.pages.map((page) => (
                      <li key={page.key}>
                        {page.title || t("trainingAdmin.page")} (
                        {page.cards.length})
                        <ul className="list-disc pl-5">
                          {page.cards.map((card) => (
                            <li key={card.key}>
                              {card.title || t("trainingAdmin.card")}
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ol>
                  <p className="text-sm text-amber-900">
                    {t("trainingAdmin.jsonReferences")}
                  </p>
                  <details className="text-sm">
                    <summary className="cursor-pointer font-semibold">
                      {t("trainingAdmin.jsonContent")}
                    </summary>
                    <pre className="mt-2 whitespace-pre-wrap break-all rounded-xl bg-white p-3 text-xs">
                      {JSON.stringify(review, null, 2)}
                    </pre>
                  </details>
                  <button
                    type="button"
                    className={primaryClass}
                    onClick={apply}
                  >
                    {t("trainingAdmin.jsonApply")}
                  </button>
                </section>
              )}
            </fieldset>
            {disabled && (
              <p className="text-sm text-slate-600">
                {t("trainingAdmin.jsonReadOnly")}
              </p>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              {t("trainingAdmin.jsonAiHint")}
            </p>
            {positions === null && <p role={positionsFailed ? "alert" : "status"} className="text-sm text-slate-700">{t(positionsFailed ? "trainingAdmin.jsonPositionsFailed" : "trainingAdmin.jsonPositionsLoading")}</p>}
            {positionsFailed && <button type="button" className={buttonClass} onClick={() => setPositionsAttempt((old) => old + 1)}>{t("trainingAdmin.reload")}</button>}
            {positions && <p className="text-sm text-slate-600">{t(positions.length ? "trainingAdmin.jsonPositionsIncluded" : "trainingAdmin.jsonPositionsEmpty", { count: positions.length })}</p>}
            {documents === null && <p role={documentsFailed ? "alert" : "status"} className="text-sm text-slate-700">{t(documentsFailed ? "trainingAdmin.jsonDocumentsFailed" : "trainingAdmin.jsonDocumentsLoading")}</p>}
            {documentsFailed && <button type="button" className={buttonClass} onClick={() => setDocumentsAttempt((old) => old + 1)}>{t("trainingAdmin.reload")}</button>}
            {documents && <p className="text-sm text-slate-600">{t(documents.length ? "trainingAdmin.jsonDocumentsIncluded" : "trainingAdmin.jsonDocumentsEmpty", { count: documents.length })}</p>}
            <PMSwitch
              variant="row"
              name="with_excerpts"
              label={t("trainingAdmin.jsonIncludeExcerpts")}
              description={t("trainingAdmin.jsonIncludeExcerptsHelp")}
              checked={withExcerpts}
              disabled={documents === null && !documentsFailed}
              onChange={(event) => setWithExcerpts(event.target.checked)}
            />
            <button
              type="button"
              className={primaryClass}
              disabled={positions === null || documents === null}
              onClick={() => copy(aiGuide)}
            >
              {t("trainingAdmin.jsonCopyAi")}
            </button>
            <pre className="whitespace-pre-wrap break-words rounded-xl bg-slate-50 p-4 text-xs leading-6">
              {aiGuide}
            </pre>
          </>
        )}
        <div className="flex justify-end">
          <button type="button" className={buttonClass} onClick={onClose}>
            {t("trainingAdmin.close")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
